import { Form, Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import InputField from "../../../atoms/InputField";
import Button from "../../../atoms/Button";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { CREATE_CASO } from "../../../../api/apiCasos";
import { CREATE_CONTRACTUAL_DOCS } from "../../../../api/apiContractualDocs";
import { mexicoStates } from "../../../../data/mexicoStates";
import { authorityOptions } from "../../../../data/authorityOptions";
import { originOptions } from "../../../../data/originOptions";
import { conceptOptions } from "../../../../data/conceptOptions";

const stateOptions = mexicoStates.map((state) => ({
    value: state.code,
    label: state.name,
}));

const ModalAddCaso = ({ refetch, onClose, empresaId }) => {
    const validationSchema = Yup.object().shape({
        comment: Yup.string().optional(),
        status: Yup.string().required("El status es requerido"),
        origin: Yup.string().required("El origen es requerido"),
        authority: Yup.string().required("La autoridad es requerida"),
        state: Yup.string().required("El estado es requerido"),
        startPeriod: Yup.date().required("La fecha de inicio es requerida"),
        endPeriod: Yup.date().required("La fecha de fin es requerida"),
        concept: Yup.array().min(1, "Debes seleccionar al menos un concepto").required("El concepto es requerido"),
        clientId: Yup.string().required("El cliente es requerido"),
    });

    const handleCreateDocs = async (id) => {
        try {
            await CREATE_CONTRACTUAL_DOCS({
                taxMatterId: id,
                service_contracts: null,
                minutes: null,
                quotation: null,
            });
            toast.warning("Documentos creados");
        } catch (error) {
            console.error(error);
            toast.warning("No se pudieron crear documentos");
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const submitData = {
                ...values,
                concept: values.concept.map((c) => c.value),
                clientId: empresaId,
            };
            const response = await CREATE_CASO(submitData);

            try {
                await handleCreateDocs(response?.id);
            } catch (error) {
                console.error("Error al crear documentos contractuales:", error);
            }

            toast.success("Registro exitoso");

            await new Promise(resolve => setTimeout(resolve, 500));

            await refetch();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar");
        } finally {
            setSubmitting(false);
        }
    };

    const initialValues = {
        comment: "",
        status: "Caso Creado",
        origin: "",
        authority: "",
        state: "",
        startPeriod: "",
        endPeriod: "",
        concept: [],
        clientId: empresaId,
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            borderColor: "#ccc",
            boxShadow: "none",
            "&:hover": {
                borderColor: "#999",
            },
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#e3f2fd" : "white",
            color: state.isSelected ? "white" : "black",
        }),
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                <Form className="w-full flex flex-col gap-4 p-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700">
                            Autoridad <span className="text-red-500">*</span>
                        </label>
                        <Select
                            name="authority"
                            options={authorityOptions}
                            styles={customSelectStyles}
                            placeholder="Selecciona una autoridad"
                            value={authorityOptions.find(
                                (opt) => opt.value === values.authority
                            )}
                            onChange={(option) =>
                                setFieldValue("authority", option?.value || "")
                            }
                            isClearable
                        />
                        {errors.authority && touched.authority && (
                            <span className="text-xs text-red-500">{errors.authority}</span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700">
                            Origen <span className="text-red-500">*</span>
                        </label>
                        <Select
                            name="origin"
                            options={originOptions}
                            styles={customSelectStyles}
                            placeholder="Selecciona un origen"
                            value={originOptions.find(
                                (opt) => opt.value === values.origin
                            )}
                            onChange={(option) =>
                                setFieldValue("origin", option?.value || "")
                            }
                            isClearable
                        />
                        {errors.origin && touched.origin && (
                            <span className="text-xs text-red-500">{errors.origin}</span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700">
                            Estado <span className="text-red-500">*</span>
                        </label>
                        <Select
                            name="state"
                            options={stateOptions}
                            styles={customSelectStyles}
                            placeholder="Selecciona un estado"
                            value={stateOptions.find(
                                (opt) => opt.value === values.state
                            )}
                            onChange={(option) =>
                                setFieldValue("state", option?.value || "")
                            }
                            isClearable
                        />
                        {errors.state && touched.state && (
                            <span className="text-xs text-red-500">{errors.state}</span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-gray-700">
                            Concepto <span className="text-red-500">*</span>
                        </label>
                        <Select
                            isMulti
                            name="concept"
                            options={conceptOptions}
                            styles={customSelectStyles}
                            placeholder="Selecciona uno o más conceptos"
                            value={values.concept}
                            onChange={(option) =>
                                setFieldValue("concept", option || [])
                            }
                        />
                        {errors.concept && touched.concept && (
                            <span className="text-xs text-red-500">{errors.concept}</span>
                        )}
                    </div>

                    <InputField
                        name="startPeriod"
                        label="Fecha de Inicio"
                        placeholder="Selecciona fecha de inicio"
                        type="date"
                    />
                    {errors.startPeriod && touched.startPeriod && (
                        <span className="text-xs text-red-500">{errors.startPeriod}</span>
                    )}

                    <InputField
                        name="endPeriod"
                        label="Fecha de Fin"
                        placeholder="Selecciona fecha de fin"
                        type="date"
                    />
                    {errors.endPeriod && touched.endPeriod && (
                        <span className="text-xs text-red-500">{errors.endPeriod}</span>
                    )}
                    <InputField
                        name="comment"
                        label="Comentario (Opcional)"
                        placeholder="Ingresa un comentario"
                        type="text"
                    />
                    <Button
                        text={
                            isSubmitting ? (
                                <PropagateLoader color="red" />
                            ) : (
                                "Registrar"
                            )
                        }
                        type="submit"
                        disabled={isSubmitting}
                        bgColor="blue"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default ModalAddCaso;